import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ConfirmDestructiveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: React.ReactNode;
  confirmValue: string;
  confirmLabel: string;
  onConfirm: (reason?: string) => void;
  isLoading?: boolean;
  reasonRequired?: boolean;
}

export default function ConfirmDestructiveDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmValue,
  confirmLabel,
  onConfirm,
  isLoading = false,
  reasonRequired = false,
}: ConfirmDestructiveDialogProps) {
  const { t } = useTranslation();
  const [value, setValue] = React.useState('');
  const [reason, setReason] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (open) {
      setValue('');
      setReason('');
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  const isConfirmed =
    value.trim() === confirmValue && (!reasonRequired || reason.trim().length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription asChild>
            <div>{description}</div>
          </DialogDescription>
        </DialogHeader>
        {reasonRequired && (
          <div className="space-y-2">
            <Label htmlFor="revoke-reason">{t('licenses.dialog.reasonForRevocation')}</Label>
            <textarea
              id="revoke-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={280}
              rows={3}
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              aria-describedby="reason-desc"
            />
            <div className="flex justify-between">
              <p id="reason-desc" className="text-xs text-muted-foreground">
                {t('licenses.dialog.reasonHint')}
              </p>
              <span className="text-xs text-muted-foreground">{reason.length}/280</span>
            </div>
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="confirm-input">
            {t('licenses.dialog.typeToConfirm', { value: confirmValue })}
          </Label>
          <Input
            id="confirm-input"
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoComplete="off"
            aria-describedby="confirm-desc"
          />
          <p id="confirm-desc" className="sr-only">
            {t('licenses.dialog.confirmSr')}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={() => onConfirm(reasonRequired ? reason.trim() : undefined)}
            disabled={!isConfirmed || isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {confirmLabel}
              </>
            ) : (
              confirmLabel
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
